import { useSpring, animated } from "react-spring";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronDown, ChevronUp, Play, Calendar, Film } from "lucide-react";

const AccordionSeason = ({ episodes, s_air_date, season_number }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { height, opacity } = useSpring({
    from: { height: 0, opacity: 0 },
    to: { height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 },
  });

  const { id } = useParams();
  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  const formattedDate = (dateString) => {
    if (!dateString) {
      return "";
    }

    const date = new Date(Date.parse(dateString));
    const options = { year: "numeric", month: "long", day: "numeric" };

    return date.toLocaleDateString(undefined, options);
  };

  return (
    <div className="border-b border-white/10 last:border-b-0 overflow-hidden">
      {/* Season Header */}
      <div
        className="flex justify-between items-center cursor-pointer hover:bg-gray/20 transition-all duration-300 group"
        onClick={toggleAccordion}
      >
        <div className="flex items-center gap-4 md:gap-6 p-4 md:p-5">
          {/* Season Number Badge */}
          <div className="flex-shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-lg bg-red text-white poppins text-xl md:text-2xl font-bold flex items-center justify-center shadow-lg shadow-red/30 group-hover:scale-105 transition-transform duration-300">
            {season_number}
          </div>
          
          {/* Season Info */}
          <div className="flex-1">
            <h3 className="mont text-white text-lg md:text-xl font-semibold mb-1">
              Season {season_number}
            </h3>
            {s_air_date && (
              <div className="flex items-center gap-2 text-white/60 mont text-sm md:text-base">
                <Calendar size={14} />
                <span>{formattedDate(s_air_date)}</span>
              </div>
            )}
            {episodes && episodes.length > 0 && (
              <p className="text-white/50 mont text-xs md:text-sm mt-1">
                {episodes.length} Episode{episodes.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        </div>
        
        {/* Toggle Icon */}
        <div className="pr-4 md:pr-6 flex items-center">
          <animated.div
            style={{
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease',
            }}
          >
            <ChevronDown className="text-white/60 group-hover:text-red transition-colors duration-300" size={24} />
          </animated.div>
        </div>
      </div>

      {/* Episodes Container */}
      <animated.div
        style={{ height, opacity }}
        className="overflow-hidden transition-all duration-300"
      >
        <div className="grid gap-2 p-4 md:p-5 bg-gray/5">
          {episodes?.map((episode) => (
            <Link
              key={episode?.episode_number}
              to={`/tvshows/${id}/${season_number}/${episode?.episode_number}`}
              className="group flex items-center gap-4 p-3 md:p-4 rounded-lg bg-gray/10 hover:bg-gray/20 border border-white/5 hover:border-red/30 transition-all duration-300 backdrop-blur-sm"
            >
              {/* Episode Poster */}
              <div className="relative flex-shrink-0 w-24 h-16 md:w-32 md:h-20 rounded-md overflow-hidden bg-gray/20 border border-white/10">
                {episode?.still_path ? (
                  <>
                    <img
                      src={`https://image.tmdb.org/t/p/w500${episode.still_path}`}
                      alt={episode?.name || `Episode ${episode?.episode_number}`}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                    />
                    {/* Fallback placeholder when image fails */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-gray/40 to-gray/20" style={{ display: 'none' }}>
                      <Film className="text-white/40 mb-1" size={24} />
                      <span className="text-white/60 mont text-xs">S{season_number}E{episode?.episode_number}</span>
                    </div>
                  </>
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-gray/40 to-gray/20">
                    <Film className="text-white/40 mb-1" size={24} />
                    <span className="text-white/60 mont text-xs">S{season_number}E{episode?.episode_number}</span>
                  </div>
                )}
                
                {/* Play Overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="bg-white/20 backdrop-blur-sm rounded-full p-2 transform scale-75 group-hover:scale-100 transition-transform duration-300">
                    <Play className="text-white" size={16} fill="white" />
                  </div>
                </div>

                {/* Episode Number Badge */}
                <div className="absolute top-1 left-1 bg-black/70 backdrop-blur-sm rounded px-1.5 py-0.5">
                  <span className="text-white mont text-xs font-semibold">
                    E{episode?.episode_number}
                  </span>
                </div>
              </div>

              {/* Episode Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h4 className="text-white mont font-semibold text-sm md:text-base group-hover:text-red transition-colors duration-300 line-clamp-2">
                    {episode?.name || `Episode ${episode?.episode_number}`}
                  </h4>
                </div>
                
                {/* Episode Meta */}
                <div className="flex flex-wrap items-center gap-3 text-xs md:text-sm">
                  {episode?.air_date && (
                    <div className="flex items-center gap-1 text-white/60 mont">
                      <Calendar size={12} />
                      <span>{formattedDate(episode.air_date)}</span>
                    </div>
                  )}
                  {episode?.runtime && (
                    <span className="text-white/60 mont">
                      {episode.runtime} min
                    </span>
                  )}
                  {episode?.vote_average > 0 && (
                    <div className="flex items-center gap-1 text-white/60 mont">
                      <span>⭐</span>
                      <span>{episode.vote_average.toFixed(1)}</span>
                    </div>
                  )}
                </div>

                {/* Episode Overview */}
                {episode?.overview && (
                  <p className="text-white/50 mont text-xs md:text-sm mt-2 line-clamp-2">
                    {episode.overview}
                  </p>
                )}
              </div>
            </Link>
          ))}
        </div>
      </animated.div>
    </div>
  );
};

export default AccordionSeason;
